import React, { useCallback, useEffect } from "react";
import {
  HashRouter as Router,
  BrowserRouter,
  Routes,
  Route,
} from "react-router-dom";
import { useImmerReducer } from "use-immer";
import { useResizeDetector } from "react-resize-detector";

// Data
import { livre, chapitres } from "./data/K782765073871";

// Context
import StateContext from "./StateContext";
import DispatchContext from "./DispatchContext";

import Accueil from "./pages/Accueil";
import Chapitre from "./pages/Chapitre";
import NotFound from "./pages/NotFound";
import VueGlobale from "./components/VueGlobale";
import ReinitParcours from "./components/ReinitParcours";
import ReinitChapter from "./components/ReinitChapter";
import MediaPlayer from "./components/MediaPlayer";

function App() {
  useEffect(() => {
    window.addEventListener("message", (e) => {
      switch (e.data.action) {
        case "close":
          e.source.close();
          break;
      }
    });
  }, []);

  useEffect(() => {
    window.addEventListener("beforeunload", clearCanvas);
    return () => {
      window.removeEventListener("beforeunload", clearCanvas);
    };
  }, []);
  const clearCanvas = () => {
    dispatch({ type: "CLEAR_ALL_CANVAS", value: true });
  };

  const initialState = {
    livre: livre,
    chapitres: chapitres,
    status: chapitres.map((chapitre, chapitreIdx) => {
      return {
        id: chapitre.id,
        locked:
          localStorage.getItem(
            `${livre.isbn}-chapter-${chapitreIdx}-locked`
          ) === null
            ? true
            : JSON.parse(
                localStorage.getItem(
                  `${livre.isbn}-chapter-${chapitreIdx}-locked`
                )
              ),
        pauses: chapitre.pauses.map((pause, pauseIdx) => {
          return {
            id: pause.id,
            contenus: pause.contenus.map((contenu, contenuIdx) => {
              return {
                locked:
                  localStorage.getItem(
                    `${livre.isbn}-content-${chapitreIdx}-${pauseIdx}-${contenuIdx}-locked`
                  ) === null
                    ? true
                    : JSON.parse(
                        localStorage.getItem(
                          `${livre.isbn}-content-${chapitreIdx}-${pauseIdx}-${contenuIdx}-locked`
                        )
                      ),
              };
            }),
          };
        }),
      };
    }),
    paths: chapitres.map((chapitre) => []),
    scrollTo: null,
    allChaptersUnlocked: false,
    vueGlobaleIsOpen: false,
    mediaPlayerIsOpen: false,
    mediaPlayerOptions: {
      mediaType: null,
      mediaSrc: null,
    },
    reinitParcoursModalIsOpen: false,
    reinitChapterModalIsOpen: false,
    reinitChapterToReset: null,
    resizeCounter: 0,
    lockCounter: 0,
    canvasClear: false,
  };

  function ourReducer(draft, action) {
    switch (action.type) {
      case "CLEAR_ALL_CANVAS":
        draft.canvasClear = action.value;
        break;
      case "SET_SCROLL_TO":
        draft.scrollTo = action.value;
      case "HANDLE_RESIZE_COUNTER":
        draft.resizeCounter++;
        break;
      case "OPEN_VUE_GLOBALE":
        draft.vueGlobaleIsOpen = true;
        break;
      case "CLOSE_VUE_GLOBALE":
        draft.vueGlobaleIsOpen = false;
        break;
      case "OPEN_MEDIA_PLAYER":
        draft.mediaPlayerIsOpen = true;
        draft.mediaPlayerOptions.mediaType = action.mediaType;
        draft.mediaPlayerOptions.mediaSrc = action.mediaSrc;
        break;
      case "CLOSE_MEDIA_PLAYER":
        draft.mediaPlayerIsOpen = false;
        break;
      case "UPDATE_CHAPTER_PATHS":
        draft.paths[action.chapterIndex] = action.value;
        break;
      case "UPDATE_CHAPTER_LOCK_STATUS":
        draft.status[action.parentIdx].locked =
          !draft.status[action.parentIdx].locked;
        draft.lockCounter++;
        break;
      case "UNLOCK_CHAPTER":
        draft.status[action.chapitreIdx].locked = false;
        break;
      case "UPDATE_CONTENT_LOCK_STATUS":
        draft.status[action.chapitreIdx].pauses[action.pauseIdx].contenus[
          action.contenuIdx
        ].locked =
          !draft.status[action.chapitreIdx].pauses[action.pauseIdx].contenus[
            action.contenuIdx
          ].locked;
        draft.lockCounter++;
        break;
      case "UPDATE_CHAPTER_LOCK_STATUS_FROM_CONTENT":
        draft.status[action.chapitreIdx].locked = action.value;
        break;
      case "UPDATE_GLOBAL_LOCK_STATUS":
        draft.allChaptersUnlocked = action.value;
        break;
      case "OPEN_REINIT_PARCOURS_MODAL":
        draft.reinitParcoursModalIsOpen = true;
        break;
      case "CLOSE_REINIT_PARCOURS_MODAL":
        draft.reinitParcoursModalIsOpen = false;
        break;
      case "REINIT_PARCOURS":
        chapitres.map((chapitre, chapitreIdx) => {
          draft.status[chapitreIdx].locked = true;
          draft.status[chapitreIdx].pauses.map((pause, pauseIdx) => {
            draft.status[chapitreIdx].pauses[pauseIdx].contenus.map(
              (contenu, contenuIdx) => {
                draft.status[chapitreIdx].pauses[pauseIdx].contenus[
                  contenuIdx
                ].locked = true;
              }
            );
          });
        });
        draft.lockCounter++;
        draft.reinitParcoursModalIsOpen = false;
        break;
      case "OPEN_REINIT_CHAPTER_MODAL":
        draft.reinitChapterToReset = action.value;
        draft.reinitChapterModalIsOpen = true;
        break;
      case "CLOSE_REINIT_CHAPTER_MODAL":
        draft.reinitChapterModalIsOpen = false;
        break;
      case "REINIT_CHAPTER":
        draft.status[draft.reinitChapterToReset].pauses.map(
          (pause, pauseIdx) => {
            draft.status[draft.reinitChapterToReset].pauses[
              pauseIdx
            ].contenus.map((contenu, contenuIdx) => {
              draft.status[draft.reinitChapterToReset].pauses[
                pauseIdx
              ].contenus[contenuIdx].locked = true;
            });
          }
        );
        draft.lockCounter++;
        draft.reinitChapterModalIsOpen = false;
        break;
      default:
        break;
    }
  }

  const [state, dispatch] = useImmerReducer(ourReducer, initialState);

  const onResize = useCallback(() => {
    dispatch({ type: "HANDLE_RESIZE_COUNTER" });
  });

  const { ref } = useResizeDetector({
    refreshMode: "debounce",
    refreshRate: 0,
    onResize,
  });

  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>
        <VueGlobale />
        <MediaPlayer />
        <ReinitParcours />
        <ReinitChapter />
        <Router>
          <div className="App" ref={ref}>
            <Routes>
              <Route path="/" element={<Accueil />} exact />
              <Route
                path="/chapitre/:chapitreId/*"
                element={<Chapitre />}
                exact
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </div>
        </Router>
      </DispatchContext.Provider>
    </StateContext.Provider>
  );
}

export default App;
